import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Lang = 'es' | 'en';

const STORAGE_KEY = 'pannini.lang';

// ─────────────────────────────────────────────────────────────────────
// Translations
// ─────────────────────────────────────────────────────────────────────
const dict = {
  // Landing
  'landing.cta.start': { es: 'Empezar gratis', en: 'Start free' },
  'landing.cta.howItWorks': { es: 'Cómo funciona', en: 'How it works' },
  'landing.cta.signIn': { es: 'Entrar', en: 'Sign in' },
  'landing.nav.brand': { es: 'Pannini 2026', en: 'Pannini 2026' },

  'landing.hero.pill': { es: 'FIFA World Cup 2026 · Junio–Julio', en: 'FIFA World Cup 2026 · Jun–Jul' },
  'landing.hero.t1': { es: 'El álbum', en: 'A full' },
  'landing.hero.t2': { es: 'completo,', en: 'album,' },
  'landing.hero.t3': { es: 'sin perder', en: 'without burning' },
  'landing.hero.t4': { es: 'la tarde.', en: 'your weekend.' },
  'landing.hero.subcopy': {
    es: 'Foto a tu página → la IA lee qué tenés y qué te falta. Matching automático con quien tenga tus repes.',
    en: 'Photograph your page → the AI reads what you have and what you\'re missing. Auto-matching with whoever has your duplicates.',
  },
  'landing.hero.subcopy.bold': { es: 'Tu álbum, en serio.', en: 'Your album, for real.' },
  'landing.hero.counter.suffix': { es: 'láminas en el álbum', en: 'stickers in the album' },
  'landing.hero.counter.countries': { es: '48 países', en: '48 nations' },
  'landing.stats.noStore': { es: 'Sin app store', en: 'No app store' },
  'landing.stats.googleLogin': { es: 'Login con Google', en: 'Google login' },
  'landing.stats.tradeNotifs': { es: 'Notifs de trade', en: 'Trade alerts' },
  'landing.scene.analyzing': { es: 'Analizando', en: 'Analyzing' },
  'landing.scene.detection': { es: 'Detección IA', en: 'AI detection' },
  'landing.scene.detected': { es: 'Detectadas', en: 'Detected' },
  'landing.scene.match.label': { es: 'Match perfecto', en: 'Perfect match' },
  'landing.scene.match.you': { es: 'Vos', en: 'You' },
  'landing.scene.match.iGive': { es: 'Te doy', en: 'I give' },
  'landing.scene.match.iGet': { es: 'Me das', en: 'I get' },
  'landing.scene.match.cta': { es: 'Proponer trade ✦', en: 'Propose trade ✦' },

  // How it works
  'landing.how.eyebrow': { es: 'Cómo funciona', en: 'How it works' },
  'landing.how.title': { es: 'Tu próximo trade en 3 pasos.', en: 'Your next trade in 3 steps.' },
  'landing.how.s1.title': { es: 'Foto a la página', en: 'Photo your page' },
  'landing.how.s1.body': { es: 'Cenital, las dos páginas abiertas. La IA lee qué laminitas pegaste y cuáles te faltan.', en: 'Top-down, both pages open. The AI reads which stickers you placed and which you\'re missing.' },
  'landing.how.s2.title': { es: 'Cargá tus repes', en: 'Add your dupes' },
  'landing.how.s2.body': { es: 'Esparcí en mesa y sacá otra foto. Cada repe se suma sola. O cargá manual si preferís.', en: 'Spread them on a table and snap a photo. Each duplicate adds itself. Or load manually.' },
  'landing.how.s3.title': { es: 'Match + trade', en: 'Match + trade' },
  'landing.how.s3.body': { es: 'Te mostramos coleccionistas con justo lo que necesitás. Tap → propuesta enviada.', en: 'We show collectors with exactly what you need. Tap → trade sent.' },

  // Differentiators
  'landing.diff.eyebrow': { es: 'Por qué Pannini', en: 'Why Pannini' },
  'landing.diff.title': { es: 'Hecho para coleccionistas serios.', en: 'Built for serious collectors.' },
  'landing.diff.f1.title': { es: 'IA que entiende el álbum', en: 'AI that gets the album' },
  'landing.diff.f1.body': { es: 'Lee la página completa: detecta país, qué slot tiene sticker pegado y cuáles te faltan.', en: 'Reads the whole page: detects country, which slots have stickers placed and which are empty.' },
  'landing.diff.f2.title': { es: 'Notificaciones en tu pantalla', en: 'Notifications on your screen' },
  'landing.diff.f2.body': { es: 'Te avisamos al instante cuando alguien quiere intercambiar con vos o aceptaron tu trade.', en: 'We notify instantly when someone proposes a trade or accepts yours.' },
  'landing.diff.f3.title': { es: 'Mismo barrio, primero', en: 'Same neighborhood first' },
  'landing.diff.f3.body': { es: 'El matching prioriza usuarios de tu ciudad para que el cambio sea fácil — café de por medio.', en: 'Matching prioritizes users in your city — make the swap over coffee.' },
  'landing.diff.f4.title': { es: 'Hitos celebrados', en: 'Milestones celebrated' },
  'landing.diff.f4.body': { es: 'Cada 25% completado dispara una notificación + confetti en la app. Coleccionar tiene que ser divertido.', en: 'Each 25% milestone fires a notification + in-app confetti. Collecting should feel like a win.' },

  // Quote
  'landing.quote.body': { es: 'Lo que tardaba <em>semanas caminando barrios</em> ahora pasa en una tarde.', en: 'What used to take <em>weeks walking around town</em> now happens in one afternoon.' },
  'landing.quote.author': { es: '— Cualquiera que haya intentado completar un álbum Panini, ever.', en: '— Anyone who has ever tried to complete a Panini album.' },

  // FAQ
  'landing.faq.eyebrow': { es: 'Preguntas', en: 'FAQ' },
  'landing.faq.title': { es: 'Antes de arrancar.', en: 'Before you start.' },
  'landing.faq.q1.q': { es: '¿Hay que descargarla de la App Store?', en: 'Do I need to download it from the App Store?' },
  'landing.faq.q1.a': { es: 'No. Pannini es una PWA: abrís el link en el celu y la podés agregar a tu pantalla principal como si fuera nativa. Pesa <b>cero KB</b> en tu teléfono.', en: 'No. Pannini is a PWA: open the link on your phone and add it to your home screen like a native app. It weighs <b>zero KB</b> on your device.' },
  'landing.faq.q2.q': { es: '¿La IA es 100% precisa?', en: 'Is the AI 100% accurate?' },
  'landing.faq.q2.a': { es: 'Tira 85–95% de accuracy en buenas condiciones. Antes de guardar te muestra lo detectado y vos confirmás — no te sorprendemos.', en: '85–95% accuracy in good conditions. We always show you what was detected so you confirm before saving — no surprises.' },
  'landing.faq.q3.q': { es: '¿Mis datos son privados?', en: 'Is my data private?' },
  'landing.faq.q3.a': { es: 'Tu colección y mail nunca son públicos. Otros sólo ven tu nombre, ciudad y foto cuando aparecés en sus matches. Hay un <b>"modo privado"</b> en Settings para esconderte completamente.', en: 'Your collection and email are never public. Others only see your name, city and photo when you appear in their matches. There\'s a <b>"private mode"</b> in Settings to hide completely.' },
  'landing.faq.q4.q': { es: '¿Cuánto cuesta?', en: 'How much does it cost?' },
  'landing.faq.q4.a': { es: 'Gratis. La IA, los matches, las notificaciones — todo. Mientras seamos pocos.', en: 'Free. The AI, the matches, the notifications — everything. While we\'re small.' },

  // Final CTA
  'landing.finalCta.t1': { es: 'Tu álbum WC2026,', en: 'Your WC2026 album,' },
  'landing.finalCta.t2': { es: 'de cero a 100%.', en: 'from zero to 100%.' },
  'landing.finalCta.subcopy': { es: 'Sin caminar barrios. Sin gastar de más en sobres random. Sólo IA + comunidad.', en: 'No walking around town. No wasted money on random packs. Just AI + community.' },
  'landing.finalCta.cta2': { es: 'Crear cuenta con mail', en: 'Sign up with email' },

  'landing.footer': { es: 'Pannini 2026 · Coleccionar es más divertido junt@s.', en: 'Pannini 2026 · Collecting is more fun together.' },

  // Settings: language section
  'settings.lang.title': { es: 'Idioma', en: 'Language' },
  'settings.lang.desc.es': { es: 'Mostrando todo en español.', en: 'Showing everything in Spanish.' },
  'settings.lang.desc.en': { es: 'Mostrando todo en inglés.', en: 'Showing everything in English.' },

  // Common
  'common.save': { es: 'Guardar', en: 'Save' },
  'common.cancel': { es: 'Cancelar', en: 'Cancel' },
  'common.retry': { es: 'Reintentar', en: 'Retry' },
  'common.delete': { es: 'Borrar', en: 'Delete' },
  'common.share': { es: 'Compartir', en: 'Share' },
  'common.copy': { es: 'Copiar', en: 'Copy' },
  'common.send': { es: 'Enviar', en: 'Send' },
  'common.skip': { es: 'Saltar', en: 'Skip' },
  'common.next': { es: 'Siguiente', en: 'Next' },
  'common.back': { es: 'Volver', en: 'Back' },
  'common.close': { es: 'Cerrar', en: 'Close' },
  'common.loading': { es: 'Cargando…', en: 'Loading…' },
  'common.saving': { es: 'Guardando…', en: 'Saving…' },
  'common.sending': { es: 'Enviando…', en: 'Sending…' },
  'common.search': { es: 'Buscar', en: 'Search' },
  'common.searchPlaceholder': { es: 'Buscar país, equipo, número, jugador…', en: 'Search country, team, number, player…' },
  'common.optional': { es: 'opcional', en: 'optional' },
  'common.from': { es: 'de', en: 'of' },
  'common.error': { es: 'Error', en: 'Error' },
  'common.unknownError': { es: 'Error desconocido. Reintentá.', en: 'Unknown error. Try again.' },
  'common.changes': { es: 'cambios sin guardar', en: 'unsaved changes' },
  'common.continue': { es: 'Continuar', en: 'Continue' },
  'common.startOver': { es: 'Empezar de nuevo', en: 'Start over' },

  // Nav
  'nav.home': { es: 'Home', en: 'Home' },
  'nav.album': { es: 'Álbum', en: 'Album' },
  'nav.upload': { es: 'Subir', en: 'Upload' },
  'nav.match': { es: 'Match', en: 'Match' },
  'nav.dupes': { es: 'Repes', en: 'Dupes' },

  // Auth: Login
  'auth.login.title': { es: 'Bienvenido', en: 'Welcome' },
  'auth.login.subtitle': { es: 'Entro para empezar a coleccionar.', en: 'I sign in to start collecting.' },
  'auth.login.orEmail': { es: 'o con email', en: 'or with email' },
  'auth.login.emailPlaceholder': { es: 'tu@email.com', en: 'you@email.com' },
  'auth.login.passwordPlaceholder': { es: 'contraseña', en: 'password' },
  'auth.login.submit': { es: 'Entrar con email', en: 'Sign in with email' },
  'auth.login.submitting': { es: 'Entrando…', en: 'Signing in…' },
  'auth.login.noAccount': { es: 'No tengo cuenta.', en: 'I don\'t have an account.' },
  'auth.login.createOne': { es: 'Crear una', en: 'Create one' },

  // Auth: Register
  'auth.register.title': { es: 'Crear cuenta', en: 'Create account' },
  'auth.register.subtitle': { es: 'Empiezo a coleccionar en segundos.', en: 'I start collecting in seconds.' },
  'auth.register.namePlaceholder': { es: 'Mi nombre', en: 'My name' },
  'auth.register.cityPlaceholder': { es: 'Mi ciudad (opcional)', en: 'My city (optional)' },
  'auth.register.submit': { es: 'Crear cuenta', en: 'Create account' },
  'auth.register.submitting': { es: 'Creando…', en: 'Creating…' },
  'auth.register.hasAccount': { es: 'Ya tengo cuenta.', en: 'I already have an account.' },
  'auth.register.signIn': { es: 'Entrar', en: 'Sign in' },

  // Dashboard
  'dash.hello': { es: 'Hola', en: 'Hi' },
  'dash.albumStatus': { es: 'Mi álbum está al', en: 'My album is at' },
  'dash.have': { es: 'Tengo', en: 'I have' },
  'dash.missing': { es: 'Faltan', en: 'Missing' },
  'dash.dupes': { es: 'Repes', en: 'Dupes' },
  'dash.uploadPhoto': { es: 'Subir foto', en: 'Upload photo' },
  'dash.uploadPhotoDesc': { es: 'La IA detecta mis laminitas.', en: 'AI detects my stickers.' },
  'dash.manualEntry': { es: 'Entrada manual', en: 'Manual entry' },
  'dash.manualEntryDesc': { es: 'Toco los números rápido.', en: 'I tap numbers fast.' },
  'dash.matches': { es: 'Matches', en: 'Matches' },
  'dash.viewAll': { es: 'Ver todos', en: 'View all' },
  'dash.uploadToFindMatches': { es: 'Cargo mis laminitas para encontrar matches.', en: 'I upload my stickers to find matches.' },
  'dash.yourDupes': { es: 'Mis repetidas', en: 'My duplicates' },
  'dash.yourDupesDesc': { es: 'Las muevo de mi carpeta al álbum de otra persona.', en: 'I move them from my folder to someone else\'s album.' },
  'dash.tradesPossible': { es: 'trades posibles', en: 'possible trades' },
  'dash.tradePossible': { es: 'trade posible', en: 'possible trade' },
  'dash.tradesPossibleDesc': { es: 'Tengo gente con la que puedo intercambiar ahora.', en: 'I have people to swap with right now.' },
  'dash.giveTo': { es: 'Me da', en: 'I get' },
  'dash.giveFrom': { es: 'Le doy', en: 'I give' },

  // Install banner
  'install.title': { es: 'Instalar la app', en: 'Install the app' },
  'install.iosHint': { es: 'Toco <icon/> abajo en Safari y luego "Agregar a inicio".', en: 'Tap <icon/> in Safari, then "Add to Home Screen".' },
  'install.androidDesc': { es: 'Dejo Pannini en mi pantalla principal como cualquier app, sin pasar por la store.', en: 'I add Pannini to my home screen like any app — no store needed.' },
  'install.cta': { es: 'Instalar', en: 'Install' },

  // Album
  'album.eyebrow': { es: 'Mi álbum', en: 'My album' },
  'album.title': { es: 'Mundial', en: 'World Cup' },
  'album.subtitle': { es: 'láminas', en: 'stickers' },
  'album.complete': { es: 'completo', en: 'complete' },
  'album.untouched': { es: 'sin cargar', en: 'not loaded' },
  'album.dupes': { es: 'repe', en: 'dupe' },
  'album.dupesPlural': { es: 'repes', en: 'dupes' },
  'album.noResults': { es: 'Sin resultados para', en: 'No results for' },
  'album.searchCountries': { es: 'País', en: 'Country' },
  'album.searchPlayers': { es: 'Jugador', en: 'Player' },

  // Manual entry
  'manual.eyebrow': { es: 'Mi álbum', en: 'My album' },
  'manual.title': { es: 'Book', en: 'Book' },
  'manual.tip': { es: 'Tap = tengo · + suma repetida · busco país o jugador.', en: 'Tap = have · + adds dupe · search country or player.' },
  'manual.statHave': { es: 'Tengo', en: 'I have' },
  'manual.statMissing': { es: 'Faltan', en: 'Missing' },
  'manual.statDupes': { es: 'Repes', en: 'Dupes' },
  'manual.statComplete': { es: 'Completo', en: 'Complete' },
  'manual.savePending': { es: 'Toco "Guardar" para sincronizar.', en: 'I tap "Save" to sync.' },

  // Matches
  'matches.eyebrow': { es: 'Mi próximo', en: 'My next' },
  'matches.title': { es: 'Intercambio', en: 'Trade' },
  'matches.titleAccent': { es: 'perfecto', en: 'is here' },
  'matches.filterAll': { es: 'Todos', en: 'All' },
  'matches.filterCity': { es: 'Mi ciudad', en: 'My city' },
  'matches.searching': { es: 'Buscando...', en: 'Searching...' },
  'matches.noMatches': { es: 'Aún no hay matches.', en: 'No matches yet.' },
  'matches.uploadMore': { es: 'Cargo más laminitas para mejorar mis chances.', en: 'I upload more stickers to improve my chances.' },
  'matches.tradeSent': { es: 'Trade enviado', en: 'Trade sent' },
  'matches.myTrades': { es: 'Mis trades', en: 'My trades' },
  'matches.viewTrades': { es: 'Ver mis trades', en: 'View my trades' },
  'matches.perfectMatch': { es: 'Perfect Match', en: 'Perfect Match' },
  'matches.strongMatch': { es: 'Strong Match', en: 'Strong Match' },
  'matches.possibleMatch': { es: 'Possible Match', en: 'Possible Match' },
  'matches.iGive': { es: 'Te doy', en: 'I give' },
  'matches.iGet': { es: 'Me das', en: 'I get' },
  'matches.you': { es: 'Yo', en: 'Me' },
  'matches.proposeTrade': { es: 'Proponer intercambio', en: 'Propose trade' },

  // TradeModal
  'tradeModal.eyebrow': { es: 'Proponer trade', en: 'Propose trade' },
  'tradeModal.with': { es: 'Con', en: 'With' },
  'tradeModal.match': { es: 'match', en: 'match' },
  'tradeModal.inPlay': { es: 'en juego', en: 'on the table' },
  'tradeModal.sticker': { es: 'lámina', en: 'sticker' },
  'tradeModal.stickers': { es: 'láminas', en: 'stickers' },
  'tradeModal.iGiveLabel': { es: 'Yo le doy', en: 'I give' },
  'tradeModal.iGiveDesc': { es: 'mis repes', en: 'of my dupes' },
  'tradeModal.iGetLabel': { es: 'Él me da', en: 'They give' },
  'tradeModal.iGetDesc': { es: 'sus repes', en: 'of their dupes' },
  'tradeModal.all': { es: 'Todas', en: 'All' },
  'tradeModal.none': { es: 'Ninguna', en: 'None' },
  'tradeModal.unfair': { es: 'Trade desbalanceado. Cuidado, suelen rechazarse.', en: 'Unbalanced trade. Careful — these often get rejected.' },
  'tradeModal.messageLabel': { es: 'Mensaje (opcional)', en: 'Message (optional)' },
  'tradeModal.messagePlaceholder': { es: '¡Hola! ¿Te interesa este cambio?', en: 'Hi! Interested in this swap?' },
  'tradeModal.send': { es: 'Enviar trade', en: 'Send trade' },
  'tradeModal.noItems': { es: 'No hay láminas.', en: 'No stickers.' },

  // Trades page
  'trades.eyebrow': { es: 'Mis', en: 'My' },
  'trades.title': { es: 'Trades', en: 'Trades' },
  'trades.tabInbox': { es: 'Recibidos', en: 'Inbox' },
  'trades.tabOutbox': { es: 'Enviados', en: 'Sent' },
  'trades.emptyInbox': { es: 'No tengo trades recibidos todavía.', en: 'No incoming trades yet.' },
  'trades.emptyOutbox': { es: 'No envié trades todavía.', en: 'I haven\'t sent any trades yet.' },
  'trades.emptyInboxDesc': { es: 'Cuando alguien me proponga uno aparecerá acá.', en: 'When someone proposes one, it shows up here.' },
  'trades.emptyOutboxDesc': { es: 'Voy a Match y propongo el primero.', en: 'I go to Match and propose my first one.' },
  'trades.from': { es: 'De', en: 'From' },
  'trades.to': { es: 'A', en: 'To' },
  'trades.acceptedTitle': { es: '¡Aceptado!', en: 'Accepted!' },
  'trades.acceptedDesc': { es: 'Coordino el encuentro y marco como completado cuando intercambiamos.', en: 'I coordinate a meetup and mark as completed when we swap.' },
  'trades.markComplete': { es: 'Marcar completado', en: 'Mark complete' },
  'trades.completed': { es: 'Intercambio completo', en: 'Trade complete' },
  'trades.rejected': { es: 'Esta propuesta fue rechazada.', en: 'This proposal was rejected.' },
  'trades.cancelled': { es: 'Propuesta cancelada.', en: 'Proposal cancelled.' },
  'trades.reject': { es: 'Rechazar', en: 'Reject' },
  'trades.accept': { es: 'Aceptar', en: 'Accept' },
  'trades.cancelProposal': { es: 'Cancelar propuesta', en: 'Cancel proposal' },
  'trades.statusPending': { es: 'Pendiente', en: 'Pending' },
  'trades.statusAccepted': { es: 'Aceptado', en: 'Accepted' },
  'trades.statusCompleted': { es: 'Completado', en: 'Completed' },
  'trades.statusRejected': { es: 'Rechazado', en: 'Rejected' },
  'trades.statusCancelled': { es: 'Cancelado', en: 'Cancelled' },

  // Duplicates
  'dupes.eyebrow': { es: 'Mi carpeta de', en: 'My folder of' },
  'dupes.title': { es: 'Repetidas', en: 'Duplicates' },
  'dupes.subtitle': { es: 'Estas son las que puedo intercambiar.', en: 'These are the ones I can trade.' },
  'dupes.uploadCta': { es: 'Subir foto de repes', en: 'Upload photo of dupes' },
  'dupes.uploadCtaDesc': { es: 'Esparzo stickers en mesa · IA suma cada uno', en: 'I spread stickers on a table · AI counts each one' },
  'dupes.empty': { es: 'Cuando tenga repetidas, aparecerán acá listas para intercambiar.', en: 'When I have duplicates, they\'ll appear here ready to trade.' },
  'dupes.byCountry': { es: 'por país', en: 'by country' },
  'dupes.totalDupes': { es: 'repes totales', en: 'total dupes' },

  // Missing
  'missing.eyebrow': { es: 'Lista de', en: 'List of' },
  'missing.title': { es: 'Faltantes', en: 'Missing' },

  // Upload page
  'upload.eyebrow': { es: 'Cargar laminitas', en: 'Load stickers' },
  'upload.title': { es: '¿Cómo querés cargar?', en: 'How do you want to load?' },
  'upload.photoTitle': { es: 'Subir foto(s)', en: 'Upload photo(s)' },
  'upload.photoBody': { es: 'Dos por país (una por página). Cargo hasta 10 juntas y la IA las procesa.', en: 'Two per country (one per page). I load up to 10 at once and AI processes them all.' },
  'upload.chooseFiles': { es: 'Elegir fotos', en: 'Choose photos' },
  'upload.optimizing': { es: 'Optimizando…', en: 'Optimizing…' },
  'upload.manualTitle': { es: 'Entrada manual', en: 'Manual entry' },
  'upload.manualBody': { es: 'Tap rápido a los números. Para cuando tengo bien claras mis repes.', en: 'Tap numbers fast. For when I know my dupes well.' },
  'upload.openGrid': { es: 'Abrir grilla', en: 'Open grid' },
  'upload.looseTitle': { es: 'Tengo repes sueltas', en: 'I have loose dupes' },
  'upload.looseBody': { es: 'Subo foto de stickers en mesa · IA suma cada uno como repetida', en: 'I upload photo of stickers on a table · AI adds each as a dupe' },
  'upload.tips.title': { es: 'Para mejor lectura:', en: 'For better detection:' },
  'upload.tips.t1': { es: '2 fotos por país — una por página del libro (izquierda y derecha)', en: '2 photos per country — one per page of the book (left and right)' },
  'upload.tips.t2': { es: 'Cargo varias juntas (hasta 10) y se procesan automáticas', en: 'I load several at once (up to 10) and they process automatically' },
  'upload.tips.t3': { es: 'Cenital, página plana, buena luz, sin sombras', en: 'Top-down, flat page, good lighting, no shadows' },
  'upload.tips.t4': { es: 'Que se vea el nombre del país arriba', en: 'Make sure the country name is visible at the top' },
  'upload.tips.t5': { es: 'Intro y FIFA Museum los cargo a mano (los foils confunden a la IA)', en: 'I load Intro and FIFA Museum manually (foils confuse the AI)' },
  'upload.gallery.eyebrow': { es: 'Mi galería', en: 'My gallery' },
  'upload.gallery.analyzing': { es: 'Analizando con IA', en: 'Analyzing with AI' },
  'upload.gallery.count': { es: 'fotos', en: 'photos' },
  'upload.gallery.done': { es: 'Listas', en: 'Done' },
  'upload.gallery.failed': { es: 'Fallidas', en: 'Failed' },
  'upload.gallery.limitReached': { es: 'Tope alcanzado', en: 'Limit reached' },
  'upload.gallery.uploadMore': { es: 'Subir más', en: 'Upload more' },
  'upload.gallery.analyze': { es: 'Analizar', en: 'Analyze' },
  'upload.gallery.processing': { es: 'Procesando', en: 'Processing' },
  'upload.review.eyebrow': { es: 'Detección', en: 'Detection' },
  'upload.review.title': { es: 'Página detectada', en: 'Page detected' },
  'upload.review.page': { es: 'Página', en: 'Page' },
  'upload.review.pageOfAlbum': { es: 'del álbum', en: 'of the album' },
  'upload.review.detectedCount': { es: 'láminas detectadas.', en: 'stickers detected.' },
  'upload.review.uncheck': { es: 'Desmarco las que NO tengo. El resto se guarda como tenidas.', en: 'I uncheck those I DON\'T have. The rest is saved as owned.' },
  'upload.review.have': { es: 'Tengo', en: 'Have' },
  'upload.review.missing': { es: 'Falta', en: 'Missing' },
  'upload.preparing': { es: 'Preparando fotos…', en: 'Preparing photos…' },
  'upload.preparingDesc': { es: 'Convirtiendo HEIC, optimizando. Tarda 2-3 segundos por foto.', en: 'Converting HEIC, optimizing. Takes 2-3 seconds per photo.' },
  'upload.warnStay': { es: 'No cierres esta pantalla', en: 'Don\'t leave this screen' },
  'upload.warnStayDesc': { es: 'Si bloqueás el celu o salís, el análisis se interrumpe.', en: 'If you lock your phone or leave, the analysis stops.' },
  'upload.review.failedBanner': { es: 'foto(s) no se pudieron leer y fueron saltadas. Volvé a cargarlas si querés.', en: 'photo(s) couldn\'t be read and were skipped. Upload again if you want.' },
  'upload.review.halfPage': { es: 'Media página detectada', en: 'Half page detected' },
  'upload.review.halfPageDesc': { es: 'Solo veo ~10 stickers. Subo la otra mitad para ver qué me falta. Lo que marco acá se suma a mi álbum.', en: 'I only see ~10 stickers. I upload the other half to see what\'s missing. What I mark here adds to my album.' },
  'upload.review.saveAndNext': { es: 'Guardar y siguiente →', en: 'Save and next →' },
  'upload.review.saveAndFinish': { es: 'Guardar y terminar', en: 'Save and finish' },
  'upload.review.reviewOf': { es: 'Revisión', en: 'Review' },
  'upload.done.title': { es: '¡Cargadas!', en: 'Loaded!' },
  'upload.done.subtitle': { es: 'Volviendo a tu álbum…', en: 'Returning to your album…' },
  'upload.error.heavy': { es: 'pesa', en: 'weighs' },
  'upload.error.read': { es: 'No pude leer las fotos.', en: 'Could not read the photos.' },
  'upload.error.tooMany': { es: 'no entraron por el tope de', en: 'didn\'t fit due to the limit of' },
  'upload.errorLabels.manual': { es: 'Manual', en: 'Manual' },
  'upload.errorLabels.notPanini': { es: 'No es Panini', en: 'Not Panini' },
  'upload.errorLabels.aiBlocked': { es: 'IA bloqueada', en: 'AI blocked' },

  // Upload Loose
  'loose.eyebrow': { es: 'Repetidas sueltas', en: 'Loose duplicates' },
  'loose.title1': { es: 'Sumo', en: 'I add' },
  'loose.titleAccent': { es: 'repes', en: 'dupes' },
  'loose.title2': { es: 'con la cámara', en: 'with the camera' },
  'loose.choose': { es: 'Elegir foto de mis repes', en: 'Pick photo of my dupes' },
  'loose.chooseDesc': { es: 'Foto cenital de stickers esparcidos en mesa. La IA suma una repe por cada uno.', en: 'Top-down photo of stickers spread on a table. AI adds one dupe for each.' },
  'loose.choosePhoto': { es: 'Elegir foto', en: 'Pick photo' },
  'loose.tips.title': { es: 'Para que funcione bien:', en: 'For best results:' },
  'loose.tips.t1': { es: 'Por el DORSO funciona mejor — el código (ARG 5, HAI 7) se ve grande', en: 'BACK side works best — the code (ARG 5, HAI 7) is clearly visible' },
  'loose.tips.t2': { es: 'Hasta 16 stickers en grilla 4×4 (filas de 4)', en: 'Up to 16 stickers in a 4×4 grid (rows of 4)' },
  'loose.tips.t3': { es: 'Sin superponer, en una superficie clara (mesa blanca)', en: 'No overlap, on a clear surface (white table)' },
  'loose.tips.t4': { es: 'Cenital (cámara desde arriba), buena luz, sin sombras', en: 'Top-down (camera from above), good light, no shadows' },
  'loose.tips.exampleLabel': { es: 'Así:', en: 'Like this:' },
  'loose.analyzing.title': { es: 'Identificando repes…', en: 'Identifying dupes…' },
  'loose.analyzing.subtitle': { es: 'Leyendo cada sticker.', en: 'Reading each sticker.' },
  'loose.review.dupesDetected': { es: 'repes detectadas', en: 'dupes detected' },
  'loose.review.dupeDetected': { es: 'repe detectada', en: 'dupe detected' },
  'loose.review.subtitle': { es: 'Desmarco las que no son mías. Cada una se suma como', en: 'I uncheck those that aren\'t mine. Each adds as' },
  'loose.review.plusOne': { es: '+1 repetida', en: '+1 dupe' },
  'loose.review.noStickers': { es: 'No reconocí ningún sticker. Pruebo una foto más nítida o con menos stickers.', en: 'No sticker recognized. I try a sharper photo or fewer stickers.' },
  'loose.review.sumCta': { es: 'Sumar', en: 'Add' },
  'loose.review.dupe': { es: 'repe', en: 'dupe' },
  'loose.review.dupes': { es: 'repes', en: 'dupes' },
  'loose.done.title': { es: '¡Sumadas!', en: 'Added!' },
  'loose.done.subtitle': { es: 'Volviendo a tus repes…', en: 'Returning to your dupes…' },

  // Settings — body
  'settings.eyebrow': { es: 'Ajustes', en: 'Settings' },
  'settings.title1': { es: 'Mi', en: 'My' },
  'settings.titleAccent': { es: 'cuenta', en: 'account' },
  'settings.share.title': { es: 'Compartir mi álbum', en: 'Share my album' },
  'settings.share.cta': { es: 'Invitar amigos', en: 'Invite friends' },
  'settings.share.copied': { es: 'Listo, copiado', en: 'Done, copied' },
  'settings.share.desc': { es: 'Les mando link con mi % completado.', en: 'I send them a link with my % completed.' },
  'settings.backup.title': { es: 'Respaldo', en: 'Backup' },
  'settings.backup.export': { es: 'Exportar', en: 'Export' },
  'settings.backup.exportDesc': { es: 'JSON con mi colección.', en: 'JSON with my collection.' },
  'settings.backup.import': { es: 'Restaurar', en: 'Restore' },
  'settings.backup.importDesc': { es: 'Subo un backup previo.', en: 'I upload a previous backup.' },
  'settings.danger.title': { es: 'Zona delicada', en: 'Danger zone' },
  'settings.reset.title': { es: 'Resetear colección', en: 'Reset collection' },
  'settings.reset.desc': { es: 'Pone todas las laminitas en 0.', en: 'Sets all stickers to 0.' },
  'settings.reset.confirm': { es: '¿Seguro? Esto borra mi progreso entero. No hay vuelta atrás (a menos que tenga backup).', en: 'Sure? This wipes my entire progress. No going back (unless I have a backup).' },
  'settings.reset.yes': { es: 'Sí, resetear', en: 'Yes, reset' },
  'settings.reset.doing': { es: 'Reseteando…', en: 'Resetting…' },
  'settings.logout': { es: 'Cerrar sesión', en: 'Sign out' },
  'settings.notif.title': { es: 'Notificaciones', en: 'Notifications' },
  'settings.notif.push': { es: 'Push notifications', en: 'Push notifications' },
  'settings.notif.unsupported': { es: 'Mi browser no soporta. Instalo la app primero.', en: 'My browser doesn\'t support it. I install the app first.' },
  'settings.notif.denied': { es: 'Permiso bloqueado. Habilito en config del browser.', en: 'Permission blocked. I enable in browser settings.' },
  'settings.notif.idle': { es: 'Avisos de trades, matches y milestones.', en: 'Alerts for trades, matches and milestones.' },
  'settings.notif.subscribed': { es: 'Activas en este dispositivo.', en: 'Active on this device.' },
  'settings.notif.sendTest': { es: 'Enviar test', en: 'Send test' },
  'settings.privacy.title': { es: 'Privacidad', en: 'Privacy' },
  'settings.privacy.private': { es: 'Modo privado', en: 'Private mode' },
  'settings.privacy.privateDesc': { es: 'No aparezco en matches de otros.', en: 'I don\'t appear in others\' matches.' },
  'settings.privacy.publicDesc': { es: 'Otros pueden hacer match con mi colección.', en: 'Others can match with my collection.' },
  'settings.demo.title': { es: 'Probar features', en: 'Try features' },
  'settings.demo.tradeModal': { es: 'Ver modal de trade (demo)', en: 'Open trade modal (demo)' },
  'settings.demo.tradeModalDesc': { es: 'Para ver cómo funciona sin necesidad de un match real.', en: 'I see how it works without needing a real match.' },
  'settings.toast.backup': { es: 'Backup descargado', en: 'Backup downloaded' },
  'settings.toast.invalid': { es: 'Archivo inválido', en: 'Invalid file' },
  'settings.toast.notRestored': { es: 'No se pudo restaurar', en: 'Could not restore' },
  'settings.toast.restored': { es: 'Restauradas', en: 'Restored' },
  'settings.toast.empty': { es: 'Ya estaba vacío', en: 'Already empty' },
  'settings.toast.reset': { es: 'Colección reseteada', en: 'Collection reset' },
  'settings.toast.notReset': { es: 'No se pudo resetear', en: 'Could not reset' },
  'settings.toast.privacyOk': { es: 'Privacidad actualizada', en: 'Privacy updated' },
  'settings.toast.privacyErr': { es: 'No se pudo guardar', en: 'Could not save' },
  'settings.toast.notifsOn': { es: 'Notificaciones activadas', en: 'Notifications enabled' },
  'settings.toast.notifsOff': { es: 'Notificaciones desactivadas', en: 'Notifications disabled' },
  'settings.toast.notifsBlocked': { es: 'Permiso bloqueado en el browser', en: 'Permission blocked in browser' },
  'settings.toast.notifsErr': { es: 'No se pudo activar', en: 'Could not enable' },
  'settings.toast.testOk': { es: 'Test enviado a', en: 'Test sent to' },
  'settings.toast.deviceS': { es: 'dispositivo', en: 'device' },
  'settings.toast.deviceP': { es: 'dispositivos', en: 'devices' },
  'settings.toast.testErr': { es: 'No se pudo entregar', en: 'Could not deliver' },
  'settings.toast.testCrash': { es: 'Error mandando test', en: 'Error sending test' },
  'settings.toast.tradeDemoSent': { es: '¡Trade demo enviado!', en: 'Demo trade sent!' },

  // Share text
  'share.text': { es: 'Este es mi álbum Panini 2026:', en: 'This is my Panini 2026 album:' },
  'share.complete': { es: 'completo. Sumate y armamos los matches:', en: 'complete. Join in and we\'ll set up matches:' },
} as const satisfies Record<string, { es: string; en: string }>;

export type I18nKey = keyof typeof dict;

// ─────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────
type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (k: I18nKey) => string };
const I18nCtx = createContext<Ctx | null>(null);

function detectLang(): Lang {
  if (typeof window === 'undefined') return 'es';
  // 1) Param de URL gana (cuando compartís ?lang=es o ?lang=en)
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  if (urlLang === 'es' || urlLang === 'en') {
    localStorage.setItem(STORAGE_KEY, urlLang);
    return urlLang;
  }
  // 2) Lo que el usuario eligió antes
  const stored = localStorage.getItem(STORAGE_KEY) as Lang | null;
  if (stored === 'es' || stored === 'en') return stored;
  // 3) Idioma del browser
  const nav = navigator.language?.toLowerCase() ?? 'es';
  if (nav.startsWith('en')) return 'en';
  return 'es';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => detectLang());

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  function setLang(l: Lang) {
    localStorage.setItem(STORAGE_KEY, l);
    setLangState(l);
  }

  function t(k: I18nKey): string {
    return dict[k][lang] ?? dict[k].es;
  }

  return <I18nCtx.Provider value={{ lang, setLang, t }}>{children}</I18nCtx.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error('useI18n must be inside I18nProvider');
  return ctx;
}
